// =============================================================================
// Tenant Time - Orari nel fuso dell'organizzazione, non in quello del browser
// =============================================================================
// Speculare a backend/src/common/time/tenant-time.util.ts, con le stesse regole.
//
// Il calendario che l'operatore guarda è quello dell'ATTIVITÀ: le 9:00 sono le
// 9:00 della sede, chiunque stia guardando e da dove. Formattare con date-fns
// (`format(d, 'HH:mm')`) usa il fuso del BROWSER: giusto solo finché operatore
// e organizzazione stanno nello stesso fuso.
//
// Tre concetti da non mescolare:
//
//   - ISTANTE: un punto nel tempo. Arriva dall'API come stringa ISO
//     ("2026-08-26T07:00:00Z") e va formattato nel fuso del tenant.
//   - DATA CIVILE ("yyyy-MM-dd"): un giorno del calendario dell'organizzazione.
//     Su di essa si fa tutta l'aritmetica (giorno +1, settimana), mai su istanti.
//   - CARRIER: una Date a mezzanotte LOCALE del browser usata solo per
//     trasportare una data civile dentro i componenti calendario esistenti
//     (MiniCalendar, DatePickerInput), che ragionano sui campi locali.
//     Un carrier NON è un istante: non va mai mandato all'API né confrontato
//     con un istante. Si converte con civilDayOf / civilDayToCarrier.
// =============================================================================

import type { Locale } from 'date-fns';
import { formatInTimeZone, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';

/** Data civile senza orario né fuso, formato "yyyy-MM-dd". */
export type CivilDate = string;

const CIVIL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;

// -----------------------------------------------------------------------------
// Istante -> wall-clock del tenant
// -----------------------------------------------------------------------------

/** Data civile su cui cade l'istante nel fuso del tenant. */
export function civilDateInTz(instant: Date, timeZone: string): CivilDate {
  return formatInTimeZone(instant, timeZone, 'yyyy-MM-dd');
}

/** Orario "HH:mm" di un istante nel fuso del tenant. */
export function timeStringInTz(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'HH:mm');
}

/** Formatta un istante nel fuso del tenant (pattern date-fns). */
export function formatInTz(
  instant: Date,
  timeZone: string,
  pattern: string,
  locale?: Locale,
): string {
  return formatInTimeZone(instant, timeZone, pattern, locale ? { locale } : undefined);
}

/** Minuti dalla mezzanotte del tenant (0-1439) per un istante. */
export function minutesOfDayInTz(instant: Date, timeZone: string): number {
  const [h, m] = formatInTimeZone(instant, timeZone, 'HH:mm').split(':').map(Number);
  return h * 60 + m;
}

/** Giorno della settimana nel fuso del tenant (0 = domenica). */
export function dayOfWeekInTz(instant: Date, timeZone: string): number {
  return civilDayOfWeek(civilDateInTz(instant, timeZone));
}

// -----------------------------------------------------------------------------
// Wall-clock del tenant -> istante
// -----------------------------------------------------------------------------

/**
 * Istante corrispondente a una data civile + minuti dalla mezzanotte, nel fuso
 * del tenant. Deve dare lo STESSO risultato dell'omologa funzione backend,
 * altrimenti client e server non concordano sugli orari ambigui:
 *  - gap di primavera: si arrotonda in avanti, mai indietro;
 *  - overlap d'autunno: si sceglie la prima occorrenza.
 *
 * La risoluzione non è delegata a `fromZonedTime` perché nell'ora ambigua quella
 * funzione dipende dal fuso dell'ambiente in cui gira — nel browser, quello di
 * chi guarda.
 */
export function wallClockToUtc(
  civilDate: CivilDate,
  minutesOfDay: number,
  timeZone: string,
): Date {
  assertCivilDate(civilDate);
  const dayOverflow = Math.floor(minutesOfDay / MINUTES_PER_DAY);
  const normalized = minutesOfDay - dayOverflow * MINUTES_PER_DAY;
  const targetDate = dayOverflow === 0 ? civilDate : addCivilDays(civilDate, dayOverflow);

  const [year, month, day] = targetDate.split('-').map(Number);
  const wallClockAsUtc = Date.UTC(
    year,
    month - 1,
    day,
    Math.floor(normalized / 60),
    normalized % 60,
  );

  const offsetBefore = getTimezoneOffset(timeZone, new Date(wallClockAsUtc - MS_PER_DAY));
  const offsetAfter = getTimezoneOffset(timeZone, new Date(wallClockAsUtc + MS_PER_DAY));

  const candidates = Array.from(new Set([offsetBefore, offsetAfter]))
    .map((offset) => new Date(wallClockAsUtc - offset))
    .filter(
      (candidate) =>
        civilDateInTz(candidate, timeZone) === targetDate &&
        minutesOfDayInTz(candidate, timeZone) === normalized,
    )
    .sort((a, b) => a.getTime() - b.getTime());

  if (candidates.length > 0) {
    return candidates[0];
  }
  return firstInstantAtOrAfterWallClock(targetDate, normalized, timeZone);
}

function firstInstantAtOrAfterWallClock(
  civilDate: CivilDate,
  minutesOfDay: number,
  timeZone: string,
): Date {
  const reachesTarget = (candidate: Date): boolean => {
    const day = civilDateInTz(candidate, timeZone);
    if (day > civilDate) return true;
    if (day < civilDate) return false;
    return minutesOfDayInTz(candidate, timeZone) >= minutesOfDay;
  };

  let lo = fromZonedTime(`${civilDate}T00:00:00.000`, timeZone).getTime();
  let hi = lo + 26 * 60 * 60 * 1000;
  while (hi - lo > 1) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (reachesTarget(new Date(mid))) hi = mid;
    else lo = mid;
  }
  return new Date(hi);
}

/** Mezzanotte di una data civile nel fuso del tenant. */
export function startOfCivilDayInTz(civilDate: CivilDate, timeZone: string): Date {
  return wallClockToUtc(civilDate, 0, timeZone);
}

/** Mezzanotte successiva (bound ESCLUSIVO) di una data civile. */
export function endOfCivilDayInTz(civilDate: CivilDate, timeZone: string): Date {
  return startOfCivilDayInTz(addCivilDays(civilDate, 1), timeZone);
}

/** Ultimo istante rappresentabile di una data civile, per i confronti inclusivi. */
export function lastInstantOfCivilDay(civilDate: CivilDate, timeZone: string): Date {
  return new Date(endOfCivilDayInTz(civilDate, timeZone).getTime() - 1);
}

// -----------------------------------------------------------------------------
// Aritmetica di calendario su date civili
// -----------------------------------------------------------------------------

export function addCivilDays(civilDate: CivilDate, days: number): CivilDate {
  const asUtc = civilDateToUtcMidnight(civilDate);
  return new Date(asUtc.getTime() + days * MS_PER_DAY).toISOString().slice(0, 10);
}

export function civilDayOfWeek(civilDate: CivilDate): number {
  return civilDateToUtcMidnight(civilDate).getUTCDay();
}

export function differenceInCivilDays(a: CivilDate, b: CivilDate): number {
  return Math.round(
    (civilDateToUtcMidnight(b).getTime() - civilDateToUtcMidnight(a).getTime()) / MS_PER_DAY,
  );
}

/** La data civile di oggi secondo il calendario dell'organizzazione. */
export function todayInTz(timeZone: string): CivilDate {
  return civilDateInTz(new Date(), timeZone);
}

// -----------------------------------------------------------------------------
// Carrier: ponte verso i componenti calendario che ragionano in ora locale
// -----------------------------------------------------------------------------

/**
 * Legge la data civile trasportata da un carrier (Date a mezzanotte locale).
 * Usa i getter LOCALI di proposito: è così che il carrier è stato costruito.
 */
export function civilDayOf(carrier: Date): CivilDate {
  const y = carrier.getFullYear();
  const m = String(carrier.getMonth() + 1).padStart(2, '0');
  const d = String(carrier.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Costruisce il carrier di una data civile: mezzanotte locale del browser. */
export function civilDayToCarrier(civilDate: CivilDate): Date {
  assertCivilDate(civilDate);
  const [y, m, d] = civilDate.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

// -----------------------------------------------------------------------------
// Etichette del fuso
// -----------------------------------------------------------------------------

/** Timezone del browser di chi sta guardando. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * true se il fuso del browser mostra un orario diverso da quello del tenant.
 * Il confronto è sull'offset attuale, non sul nome: due identificativi diversi
 * (es. Europe/Rome e Europe/Paris) danno lo stesso orario e non meritano nessun
 * avviso all'utente.
 */
export function timeZonesDiffer(tenantTimeZone: string, otherTimeZone: string): boolean {
  if (tenantTimeZone === otherTimeZone) return false;
  const now = new Date();
  return (
    formatInTimeZone(now, tenantTimeZone, 'yyyy-MM-dd HH:mm') !==
    formatInTimeZone(now, otherTimeZone, 'yyyy-MM-dd HH:mm')
  );
}

/** Sigla del fuso da mostrare accanto a un orario (es. "CEST", "GMT+2"). */
export function timeZoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  return formatInTimeZone(at, timeZone, 'zzz');
}

/**
 * Offset dal meridiano di Greenwich in minuti, con segno (Roma d'estate = +120).
 * Dipende dalla data, perché cambia con l'ora legale.
 */
export function timeZoneOffsetMinutes(timeZone: string, at: Date = new Date()): number {
  return getTimezoneOffset(timeZone, at) / 60000;
}

/**
 * Offset in formato "GMT+02:00", come lo si legge nei selettori di fuso.
 * I minuti sono espliciti perché non tutti i fusi sono a scatti di un'ora:
 * l'India è a +05:30, il Nepal a +05:45, le Chatham a +12:45.
 */
export function formatGmtOffset(timeZone: string, at: Date = new Date()): string {
  const minutes = timeZoneOffsetMinutes(timeZone, at);
  if (minutes === 0) return 'GMT';
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `GMT${sign}${hh}:${mm}`;
}

/**
 * Alcuni identificativi IANA conservano il nome storico della città: il tzdata
 * non li rinomina mai, per non rompere i dati già salvati. Mostrarli così com'è
 * è però disorientante — chi cerca "Kolkata" non troverebbe "Asia/Calcutta" —
 * quindi in interfaccia si usa il nome attuale. Il VALORE salvato resta sempre
 * l'identificativo canonico restituito e validato dal server.
 */
const MODERN_ZONE_NAMES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Godthab': 'America/Nuuk',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
};

/**
 * Nome leggibile di un fuso: "Europe/Rome" -> "Europe / Rome".
 * Per i fusi rinominati mostra il nome attuale (vedi MODERN_ZONE_NAMES).
 */
export function timeZoneLabel(timeZone: string): string {
  const display = MODERN_ZONE_NAMES[timeZone] ?? timeZone;
  return display.replace(/_/g, ' ').replace('/', ' / ');
}

/**
 * Termini aggiuntivi su cui far corrispondere la ricerca di un fuso: il nome
 * storico e quello moderno devono funzionare entrambi.
 */
export function timeZoneSearchAliases(timeZone: string): string {
  const modern = MODERN_ZONE_NAMES[timeZone];
  return modern ? `${timeZone} ${modern}` : timeZone;
}

// -----------------------------------------------------------------------------
// Helper interni
// -----------------------------------------------------------------------------

function civilDateToUtcMidnight(civilDate: CivilDate): Date {
  assertCivilDate(civilDate);
  return new Date(`${civilDate}T00:00:00.000Z`);
}

function assertCivilDate(value: string): void {
  if (!CIVIL_DATE_RE.test(value)) {
    throw new Error(`Data civile non valida: "${value}" (atteso "yyyy-MM-dd")`);
  }
  // "2026-02-31" è ben formato ma non esiste: senza questo controllo le Date di
  // JavaScript lo farebbero slittare in silenzio al 3 marzo.
  const [year, month, day] = value.split('-').map(Number);
  const asUtc = new Date(Date.UTC(year, month - 1, day));
  if (
    asUtc.getUTCFullYear() !== year ||
    asUtc.getUTCMonth() !== month - 1 ||
    asUtc.getUTCDate() !== day
  ) {
    throw new Error(`Data civile inesistente: "${value}"`);
  }
}
